import React, { useRef } from 'react';
import { BiFullWidthButton, useBiWidthClass } from '../BiFullWidth';
import { useNavigate } from 'react-router-dom';
import { BuildingOffice2Icon, BuildingOfficeIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/20/solid';
import TitleSelect, { TitleSelectAction, TitleSelectOption, TitleSelectSearch } from '@ui/selector/TitleSelect';
import { BiPropertyGroup, BiScope } from '@app/model/bi/Bi';
import { propertyCount, shortName } from '../Bi.utilities';
import PropertyGroupsDrawer, { PropertyGroupsDrawerHandle } from './PropertyGroupsDrawer';

type Props = {
	scope: BiScope,
	// null = all properties, or the properties of the Property group.
	companyHotelId: number | null,
	// The selected Property group (BI Property groups); null: one property or all properties.
	propertyGroup?: BiPropertyGroup | null,
	// The Property groups that the user can choose, in alphabetical order.
	groups?: BiPropertyGroup[],
	// true: the user is a company admin; the list ends with "Manage property groups".
	canEditGroups?: boolean,
	// The address query of the page; the selector changes only the hotel in it.
	search: string,
	sublabel: string,
	// false: the title is plain text, with no list of properties (a page that does not depend on the scope).
	selectable?: boolean,
	// The title instead of the scope name.
	title?: string,
	// The name of the page for a screen reader (the h1), for example "Segmentation".
	page: string,
	// The controls on the right (period, groups).
	children?: React.ReactNode,
	// ROO2-288 D11: one hotel at a time, so the list holds the properties only (no All properties, no Property groups).
	propertiesOnly?: boolean,
	// ROO2-283: the Full width switch of the BI reports. It shows from a 1,376 px window.
	fullWidthSwitch?: boolean,
};

// The search field of the list shows when the company has more than 7 properties (grill decision 6). It finds a property
// by its name and its city; "All properties" stays on top (decision 4). minOptions counts the pinned "All properties" too.
const SEARCH: TitleSelectSearch = { placeholder: 'Search your properties', empty: 'No property matches', minOptions: 8 };

const GROUP = 'group:';

// A property or All properties sets ?hotel= and removes ?group=; a Property group sets ?hotel=all&group=<id>.
const hrefOf = (search: string, choice: string) => {
	const params = new URLSearchParams(search);
	if (choice.startsWith(GROUP)) {
		params.set('hotel', 'all');
		params.set('group', choice.slice(GROUP.length));
	} else {
		params.set('hotel', choice);
		params.delete('group');
	}
	return `?${params.toString()}`;
};

// The page header of a BI report: the scope (all properties, a Property group or one hotel) is the page title, and it
// opens the list of the company (TitleSelect): All properties, the part "Property groups" and the part "Properties" (picker
// direction B). Each property shows the source of its reservations, each group its number of properties. A choice changes
// only the scope in the address; BiLayout gives the last chosen hotel to the app when the user leaves BI (a group changes
// nothing there). A company admin also gets "Manage property groups" under the list: it opens the group editor.
const BiPageHeader: React.FunctionComponent<Props> = ({
	scope, companyHotelId, propertyGroup = null, groups = [], canEditGroups = false, search, sublabel, selectable = true, title, page, children,
	propertiesOnly = false, fullWidthSwitch = false,
}) => {
	const width = useBiWidthClass();
	const navigate = useNavigate();
	const editor = useRef<PropertyGroupsDrawerHandle>(null);
	const property = scope.properties.find(p => p.companyHotelId === companyHotelId);
	const label = title ?? propertyGroup?.title ?? (property === undefined ? 'All properties' : shortName(property, scope.companyName));
	// The headings show only with at least one group: a company with no group keeps the list of today.
	const sections = groups.length > 0 && !propertiesOnly;
	const options: TitleSelectOption[] = [
		...(propertiesOnly ? [] : [{ id: 'all', label: 'All properties', detail: `${scope.companyName} · ${propertyCount(scope.properties.length)}`, icon: BuildingOffice2Icon, pinned: true }]),
		...(propertiesOnly ? [] : groups).map(g => ({
			id: `${GROUP}${g.id}`,
			label: g.title,
			detail: propertyCount(g.properties.length),
			icon: RectangleGroupIcon,
			section: 'Property groups',
		})),
		...scope.properties.map(p => ({
			id: String(p.companyHotelId),
			label: shortName(p, scope.companyName),
			detail: `${p.area} · ${p.reservationsSource === null ? 'No reservations' : `${p.reservationsSource} reservations`}`,
			icon: BuildingOfficeIcon,
			searchText: `${p.name} ${p.area}`,
			section: sections ? 'Properties' : undefined,
		})),
	];
	const value = propertyGroup !== null ? `${GROUP}${propertyGroup.id}` : (companyHotelId === null ? 'all' : String(companyHotelId));
	const action: TitleSelectAction | undefined = canEditGroups && selectable && !propertiesOnly
		? { label: 'Manage property groups', icon: AdjustmentsHorizontalIcon, onClick: () => editor.current?.open(propertyGroup?.id ?? null) }
		: undefined;

	return <header className="border-b border-gray-200 bg-white">
		<h1 className="sr-only">{title === undefined ? `${page} · ${label}` : page}</h1>
		<div className={`${width} flex flex-col gap-3 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8`}>
			<div className="flex min-w-0 items-start">
				<TitleSelect options={options} value={value} label={label} sublabel={sublabel} listLabel="Properties" disabled={!selectable}
					search={SEARCH} action={action} onChange={choice => navigate(hrefOf(search, choice))} />
				{canEditGroups && selectable && <PropertyGroupsDrawer ref={editor} scope={scope} />}
			</div>
			{(children !== undefined || fullWidthSwitch) && <div className="flex flex-wrap items-center gap-3">{children}
				{fullWidthSwitch && <BiFullWidthButton />}</div>}
		</div>
	</header>;
};

export default BiPageHeader;
